// src/data/applicationNotice.js
//
// ONB-1: THE ACCURACY NOTICE AND THE THREE ACKNOWLEDGEMENTS.
//
// The callable already refused an application that arrived without accuracyNoticeVersion, and ONB-5
// already recorded which version was shown. Neither could say WHAT WAS SHOWN, because the text
// existed only in data\YCP_ONBOARDING_REQUIREMENTS_v4_2026-09-20.md - a document. An applicant is
// held to what he ticked; the platform could not have produced it.
//
// The wording here is Bob's, carried from requirements v4 (approved 2026-09-21, B-5) without
// paraphrase. Directives section XI: the form shows THIS, the server checks THIS, the onboarding
// record names THIS version. One source, three readers. Nobody retypes it.
//
// PURE. No Firestore, no network, no clock.

// Bumped ONLY when the text below changes, and never edited in place: an applicant who ticked v1
// ticked v1's words, and a record naming a version whose text has since been rewritten says nothing.
// A new version is a new key; the old text stays in the file so a record can still be read back.
export const NOTICE_VERSION = 'v1';

// Requirements v4, "Also at the application": the accuracy notice, shown before submission.
export const ACCURACY_NOTICE = Object.freeze({
  v1: 'Current, accurate information is required of everyone at every level - members included. '
    + 'Check your own seniority number and dates before you submit; an error here follows your claims.',
});

// Requirements v4: "Acknowledgements, ticked". Three, and each carries what it is for, so a later
// reader cannot add a fourth without stating one (the same test Bob set for the fields, ruling 193.5).
export const ACKNOWLEDGEMENTS = Object.freeze([
  Object.freeze({
    key: 'records_not_represents',
    text: 'I understand that this platform RECORDS. It does not represent me. '
        + 'My representative is my Local Chairperson or General Chairperson, not this application.',
    why: 'nobody may mistake a filing tool for a representative',
  }),
  Object.freeze({
    key: 'ai_can_be_wrong',
    text: 'I understand that AI can be wrong, including this application, and that nothing it produces '
        + 'is a legal opinion or a substitute for my representative reading it.',
    why: 'ruling 135',
  }),
  Object.freeze({
    key: 'information_use',
    text: 'I understand how my information is used and who sees it: my Local Chairperson, my General '
        + 'Chairperson, and the administrator. It is not sold, and it is not shown to the carrier '
        + 'except in a claim filed on my behalf.',
    why: 'he is told who reads what he writes, before he writes it',
  }),
]);

export function acknowledgementKeys() {
  return ACKNOWLEDGEMENTS.map((a) => a.key);
}

export function noticeText(version) {
  return ACCURACY_NOTICE[String(version || NOTICE_VERSION)] || '';
}

// What a tick RECORDS: the version whose words he actually saw, never a bare `true`. A boolean says
// he agreed to something; this says to what. ONB-5 reads these back years later.
export function tickValue() {
  return NOTICE_VERSION;
}

// The gate. Every acknowledgement must carry a KNOWN version - an unticked box, a `false`, or a
// version whose text this file no longer holds are all refusals, and each is named separately
// because "please tick the boxes" does not tell a man which box.
export function validateAcknowledgements(acknowledgements, accuracyNoticeVersion) {
  const ack = acknowledgements || {};
  const shown = String(accuracyNoticeVersion || '');
  const missing = [];
  const unknownVersion = [];
  for (const a of ACKNOWLEDGEMENTS) {
    const v = ack[a.key];
    if (!v || v === true) { missing.push(a.key); continue; }
    if (!ACCURACY_NOTICE[String(v)]) unknownVersion.push(a.key);
  }
  const noticeUnknown = !shown || !ACCURACY_NOTICE[shown];
  return {
    ok: missing.length === 0 && unknownVersion.length === 0 && !noticeUnknown,
    missing,
    unknownVersion,
    noticeUnknown,
  };
}
